import cx from "classnames";
import { ResultOf } from "gql.tada";
import { motion } from "motion/react";
import { SolutionsHeroById } from "../../queries/entries/SolutionsHeroById";
import { animationCurves } from "../../utils/animationCurves";
import { MediaItem } from "../shared/MediaItem";
import { RichText } from "../shared/core/RichText";

const transition = animationCurves.macro;

const variants = [
  {
    initial: {
      y: "100%",
      scale: 0.4,
      x: "84px",
    },
    animate: {
      scale: 1,
      y: 0,
      rotate: -5,
      x: "64px",
    },
    transition,
  },
  {
    initial: {
      y: "100%",
      scale: 0.4,
    },
    animate: {
      scale: 1,
      y: -56,
    },
    transition: {
      delay: 0.2,
      ...transition,
    },
  },
  {
    initial: {
      y: "100%",
      scale: 0.4,
      x: "-84px",
    },
    animate: {
      scale: 1,
      y: 0,
      rotate: 5,
      x: "-64px",
    },
    transition,
  },
];

export const SolutionsHero = ({
  entry: { solutionsHero: entry },
}: {
  entry: ResultOf<typeof SolutionsHeroById>;
}) => {
  if (!entry) return null;

  return (
    <section className="w-full h-full min-h-screen">
      <div
        className={cx([
          "w-full min-h-full",
          "overflow-x-hidden",
          "flex justify-center items-center flex-col",
          "pt-[224px] dsk:pt-[247px] pb-[132px] dsk:pb-[157px]",
          "bg-gradient-to-t from-palette-current-deep/60 to-palette-current-deep/0",
        ])}
      >
        <div className="flex relative top-6 z-0 w-[155%] justify-center dsk:w-full">
          {entry.solutionsHeroEyebrowImages?.items?.map((image, index) => (
            <motion.div
              key={index}
              initial={variants[index].initial}
              animate={variants[index].animate}
              transition={transition}
              className={cx(
                "h-[106px] aspect-video overflow-hidden rounded-lg brightness-125"
              )}
            >
              <MediaItem
                item={image}
                className="w-full h-full object-cover"
                width={800}
              />
            </motion.div>
          ))}
        </div>
        <div className="z-10 px-[18px] dsk:px-0 relative">
          <div className="overflow-hidden">
            <motion.h1
              className="text-heading-1 dsk:text-display uppercase text-center max-w-[1280px] text-balance"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              transition={transition}
            >
              {entry.solutionsHeroTitle}
            </motion.h1>
          </div>
          {entry.bodyRichText && (
            <motion.div
              className="max-w-[450px] text-center mt-[30px] mx-auto"
              initial={{ y: "60%" }}
              animate={{ y: 0 }}
              transition={transition}
            >
              <RichText content={entry.bodyRichText} />
            </motion.div>
          )}
        </div>
      </div>
      {entry.fullWidthImage && (
        <div className="aspect-video w-full overflow-hidden">
          <motion.div
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={transition}
          >
            <MediaItem
              item={entry.fullWidthImage}
              className="w-full h-full object-cover"
              width={1920}
            />
          </motion.div>
        </div>
      )}
    </section>
  );
};
